import { CartStore } from "assets/cart";
import { InitContentSlider } from "assets/content-slider";
import { EditorStore } from "assets/editor";
import { ModalStore } from "assets/modals";
import { JSONParse } from "assets/utils";
import { CardsProductBlock } from "types/blocks";
import { _Product_hydrated, _Variant_hydrated } from "./types";

export type ProductFunctions = typeof _product;

export const _product = {
  getHtmlProduct: (handle) => {
    const product = JSONParse<_Product_hydrated>(
      document.querySelector(`[data-primary-product-data="${handle}"]`)?.innerHTML ??
        document.querySelector(`[data-product-data="${handle}"]`)?.innerHTML
    );

    if (product) {
      _products[handle] = {
        _recommendations_loaded_at: 0,
        complementary_products: [],
        related_products: [],
        ...(_products[handle] ?? {}),
        ...(product?._full_data ? product : _products[handle]?._full_data ? _products[handle] : product),
        _updated_at: Date.now(),
      };
      if (!_products[handle]?._full_data) {
        const dbKey = `_${window.Shopify.theme.id}-product--${handle}`;

        idbKeyval.get<_Product_hydrated>(dbKey).then((dbProduct) => {
          if (dbProduct?._full_data) {
            _products[handle] = {
              ...(_products[handle] ?? {}),
              ...dbProduct,
              _updated_at: Date.now(),
            };
            _product.saveProduct(handle);
          }
        });
      }
      _product.saveProduct(handle);

      return _products[handle];
    }
    return null;
  },
  getCachedProduct: async (handle) => {
    const dbKey = `_${window.Shopify.theme.id}-product--${handle}`;
    const product = await idbKeyval.get<_Product_hydrated>(dbKey);

    if (product && (product._updated_at ?? 0) > Date.now() - 1000 * 60 * 30) {
      _products[handle] = {
        ...(_products[handle] ?? {}),
        ...(product?._full_data ? product : _products[handle]?._full_data ? _products[handle] : product),
      };
      return _products[handle];
    }

    return null;
  },
  getFetchProduct: async (handle: string, productId: number) => {
    if (!productId) return null;

    try {
      if (!window._fetch_products.has(handle)) {
        window._fetch_products.set(
          handle,
          fetch(
            `/recommendations/products?product_id=${productId}&limit=10&section_id=data_product_json&intent=related&with_product_data=true`
          )
            .then((res) => res.text())
            .then((text) => {
              const html = document.createElement("div");
              html.innerHTML = text;

              const product = JSONParse<_Product_hydrated>(html.querySelector("[data-product-data]")?.innerHTML ?? "{}");
              product.related_products = JSONParse<_Product_hydrated[]>(
                html.querySelector("[data-product-recommendations]")?.innerHTML ?? "[]"
              );

              requestIdleCallback(
                async () => {
                  [...(product.related_products ?? [])]
                    .filter((a, i, arr) => arr.findIndex((b) => a.handle === b.handle) === i)
                    .map(async (product) => {
                      const dbKey = `_${window.Shopify.theme.id}-product--${product.handle}`;
                      _products[product.handle] = {
                        _recommendations_loaded_at: 0,
                        related_products: [],
                        complementary_products: [],
                        ...(_products[product.handle] ?? ((await idbKeyval.get(dbKey)) || {})),
                        ...(product?._full_data
                          ? product
                          : _products[product.handle]?._full_data
                          ? _products[product.handle]
                          : product),
                        _updated_at: Date.now(),
                      };
                      idbKeyval.set(dbKey, _products[product.handle]);
                    });
                },
                { timeout: 5000 }
              );

              product._recommendations_loaded_at = Date.now();
              return product;
            })
        );
      }

      const product = await window._fetch_products.get(handle);

      if (product) {
        _products[handle] = {
          complementary_products: [],
          ...(_products[handle] ?? {}),
          ...(product?._full_data ? product : _products[handle]?._full_data ? _products[handle] : product),
          _recommendations_loaded_at: product._recommendations_loaded_at,
          related_products: product.related_products,
          _updated_at: Date.now(),
        };
        _product.saveProduct(handle);
        return _products[handle];
      }
      return null;
    } catch (err) {
      return null;
    }
  },
  saveProduct: (handle, dataOverride = undefined) => {
    _products[handle] = {
      ...(_products[handle] ?? {}),
      ...(dataOverride ?? {}),
    };

    const product = _products[handle];
    if (!product) return null;

    const dbKey = `_${window.Shopify.theme.id}-product--${handle}`;

    const nextSeq = (window._save_product_sequence.get(handle) ?? 0) + 1;
    window._save_product_sequence.set(handle, nextSeq);

    if (window._save_product_schedule.has(handle)) {
      return product;
    }

    window._save_product_schedule.add(handle);

    requestIdleCallback(
      async () => {
        try {
          // eslint-disable-next-line no-constant-condition
          while (true) {
            const sequence_before = window._save_product_sequence.get(handle) ?? 0;
            const latest = _products[handle];
            await idbKeyval.set(dbKey, latest);

            const sequence_after = window._save_product_sequence.get(handle) ?? 0;
            if (sequence_after === sequence_before) {
              break;
            }
          }
        } finally {
          window._save_product_schedule.delete(handle);
        }
      },
      { timeout: 5000 }
    );

    return product;
  },
  getHydratedProductData: async (handle: string, productId: number) => {
    if (!_products[handle] || (_products[handle]?._recommendations_loaded_at ?? 0) < Date.now() - 1000 * 60 * 30) {
      _product.getHtmlProduct(handle);
    }
    if (!_products[handle] || (_products[handle]?._recommendations_loaded_at ?? 0) < Date.now() - 1000 * 60 * 30) {
      await _product.getCachedProduct(handle);
    }
    if (!_products[handle] || (_products[handle]?._recommendations_loaded_at ?? 0) < Date.now() - 1000 * 60 * 30) {
      await _product.getFetchProduct(handle, productId || _products[handle]?.id);
    }
    if (!_products[handle] || (_products[handle]?._recommendations_loaded_at ?? 0) < Date.now() - 1000 * 60 * 30) {
      return null;
    }
    return _products[handle];
  },
  getProductData: async (handle: string, productId: number) => {
    if (!_products[handle]) {
      _product.getHtmlProduct(handle);
    }
    if (!_products[handle]) {
      await _product.getCachedProduct(handle);
    }
    if (!_products[handle]) {
      await _product.getFetchProduct(handle, productId);
    }
    if (!_products[handle]) {
      return null;
    }
    return _products[handle];
  },
  initDynamicProductCards: (
    $el: HTMLElement,
    {
      targeting_type,
      target_product_handle,
      primary_source,
      fallback_source,
      product_class,
      desktop_display_limit = 50,
      mobile_display_limit = 50,
      addon_products,
      addon_auto_add,
      addon_bundle_in_cart,
      addon_target_product,
      hide_if_empty,
      handleResize = () => {},
      order_offset = 1,
      section_id,
      filter_by_type = "",
      hide_out_of_stock = false,
      block_id,
    }: {
      targeting_type: CardsProductBlock["settings"]["targeting_type"];
      target_product_handle?: string;
      primary_source: "complementary" | "related" | "recently_viewed" | "manual";
      fallback_source: "complementary" | "related" | "recently_viewed" | "manual";
      product_class: string;
      filter_by_type: string;
      desktop_display_limit?: number;
      mobile_display_limit?: number;
      order_offset?: number;
      hide_out_of_stock?: boolean;
      addon_products: boolean;
      addon_auto_add: boolean;
      addon_bundle_in_cart: boolean;
      addon_target_product?: string;
      hide_if_empty: "none" | "section" | "container" | "block";
      section_id: string;
      block_id: string;
      handleResize: ReturnType<InitContentSlider>["content_slider"]["handleResize"];
    }
  ) => {
    const container = $el.parentElement;
    const fallback_products = JSONParse<_Product_hydrated[]>($el.getAttribute(`data-fallback-products`)) ?? [];
    const limit = Math.max(desktop_display_limit, mobile_display_limit);

    const product = JSONParse<
      _Product_hydrated & {
        selected_or_first_available_variant_id?: number;
        selected_variant_id?: number;
      }
    >(document.querySelector(`[data-product-data="${target_product_handle}"]`)?.innerHTML);

    const cart = window.Alpine.store("cart") as CartStore;
    const modal = window.Alpine.store("modal") as ModalStore;
    const editor = window.Alpine.store("editor") as EditorStore;

    const state = window.Alpine.reactive({
      product: product as _Product_hydrated,
    });

    const renderDynamicProductCards = async () => {
      handleResize();
      setTimeout(handleResize, 50);

      const lineItemProducts = /^cart_/gi.test(targeting_type)
        ? await Promise.all(cart.state.items?.map((item) => _product.getHydratedProductData(item.handle, item.product_id)))
        : [];

      const recentlyViewedProducts = /^recently_viewed_/gi.test(targeting_type)
        ? await Promise.all(_recent_products?.map(([handle, product_id]) => _product.getHydratedProductData(handle, product_id)))
        : [];

      const products: _Product_hydrated[] = [];
      const complementary_products = [];
      const related_products = [];

      switch (targeting_type) {
        case "product": {
          state.product?.complementary_products?.forEach((item) => {
            if (hide_out_of_stock && !item.available) return;
            if (item.id !== state.product?.id) {
              complementary_products.push(item);
            }
          });
          state.product?.related_products?.forEach((item) => {
            if (hide_out_of_stock && !item.available) return;
            if (item.id !== state.product?.id) {
              related_products.push(item);
            }
          });
          break;
        }
        case "recently_viewed_ai": {
          const expensive = [...recentlyViewedProducts].sort((a, b) => b.price - a.price).slice(0, 3);
          const recent = recentlyViewedProducts.slice(0, 2).sort((a, b) => b.price - a.price);

          [...recent, ...expensive]?.forEach((product, parentIndex) => {
            product?.complementary_products?.forEach((item, i) => {
              if (hide_out_of_stock && !item.available) return;
              if (i >= 2 || (i >= 1 && parentIndex >= 2)) {
                return;
              }
              complementary_products.push(item);
            });
            product?.related_products?.forEach((item, i) => {
              if (hide_out_of_stock && !item.available) return;
              if (i >= 2 || (i >= 1 && parentIndex >= 2)) {
                return;
              }
              related_products.push(item);
            });
          });
          break;
        }
        case "recently_viewed_most_expensive": {
          [...recentlyViewedProducts]
            .sort((a, b) => b.price - a.price)
            ?.forEach((product, parentIndex) => {
              product?.complementary_products?.forEach((item, i) => {
                if (hide_out_of_stock && !item.available) return;
                if (i >= 2 || (i >= 1 && parentIndex >= 2)) {
                  return;
                }
                complementary_products.push(item);
              });
              product?.related_products?.forEach((item, i) => {
                if (hide_out_of_stock && !item.available) return;
                if (i >= 2 || (i >= 1 && parentIndex >= 2)) {
                  return;
                }
                related_products.push(item);
              });
            });
          break;
        }
        case "recently_viewed_least_expensive": {
          [...recentlyViewedProducts]
            .sort((a, b) => a.price - b.price)
            ?.forEach((product, parentIndex) => {
              product?.complementary_products?.forEach((item, i) => {
                if (hide_out_of_stock && !item.available) return;
                if (i >= 2 || (i >= 1 && parentIndex >= 2)) {
                  return;
                }
                complementary_products.push(item);
              });
              product?.related_products?.forEach((item, i) => {
                if (hide_out_of_stock && !item.available) return;
                if (i >= 2 || (i >= 1 && parentIndex >= 2)) {
                  return;
                }
                related_products.push(item);
              });
            });
          break;
        }
        case "cart_ai": {
          const expensive = [...cart.state.items].sort((a, b) => b.final_price - a.final_price).slice(0, 3);
          const recent = cart.state.items.slice(0, 2).sort((a, b) => b.final_price - a.final_price);

          [...recent, ...expensive]?.forEach((line, parentIndex) => {
            const product = lineItemProducts.find((p) => p.handle === line.handle);

            product?.complementary_products?.forEach((item, i) => {
              if (hide_out_of_stock && !item.available) return;
              if (i >= 2 || (i >= 1 && parentIndex >= 2)) {
                return;
              }
              complementary_products.push(item);
            });
            product?.related_products?.forEach((item, i) => {
              if (hide_out_of_stock && !item.available) return;
              if (i >= 2 || (i >= 1 && parentIndex >= 2)) {
                return;
              }
              related_products.push(item);
            });
          });
          break;
        }
        case "cart_most_expensive": {
          [...cart.state.items]
            .sort((a, b) => b.final_price - a.final_price)
            ?.forEach((line, parentIndex) => {
              const product = lineItemProducts.find((p) => p.handle === line.handle);

              product?.complementary_products?.forEach((item, i) => {
                if (hide_out_of_stock && !item.available) return;
                if (i >= 2 || (i >= 1 && parentIndex >= 2)) {
                  return;
                }
                complementary_products.push(item);
              });
              product?.related_products?.forEach((item, i) => {
                if (hide_out_of_stock && !item.available) return;
                if (i >= 2 || (i >= 1 && parentIndex >= 2)) {
                  return;
                }
                related_products.push(item);
              });
            });
          break;
        }
        case "cart_least_expensive": {
          [...cart.state.items]
            .sort((a, b) => a.final_price - b.final_price)
            ?.forEach((line, parentIndex) => {
              const product = lineItemProducts.find((p) => p.handle === line.handle);

              product?.complementary_products?.forEach((item, i) => {
                if (hide_out_of_stock && !item.available) return;
                if (i >= 2 || (i >= 1 && parentIndex >= 2)) {
                  return;
                }
                complementary_products.push(item);
              });
              product?.related_products?.forEach((item, i) => {
                if (hide_out_of_stock && !item.available) return;
                if (i >= 2 || (i >= 1 && parentIndex >= 2)) {
                  return;
                }
                related_products.push(item);
              });
            });
          break;
        }
        case "cart_recently_added": {
          cart.state.items?.forEach((line, parentIndex) => {
            const product = lineItemProducts.find((p) => p.handle === line.handle);

            product?.complementary_products?.forEach((item, i) => {
              if (hide_out_of_stock && !item.available) return;
              if (i >= 2 || (i >= 1 && parentIndex >= 2)) {
                return;
              }
              complementary_products.push(item);
            });
            product?.related_products?.forEach((item, i) => {
              if (hide_out_of_stock && !item.available) return;
              if (i >= 2 || (i >= 1 && parentIndex >= 2)) {
                return;
              }
              related_products.push(item);
            });
          });
          break;
        }
      }

      switch (primary_source) {
        case "complementary": {
          complementary_products?.forEach((prod) => products.push(prod));
          break;
        }
        case "related": {
          related_products?.forEach((prod) => products.push(prod));
          break;
        }
        case "recently_viewed": {
          const recent_products = await Promise.all(
            (window?._recent_products ?? [])?.map(([handle, product_id]) => _product.getHydratedProductData(handle, product_id))
          );
          recent_products?.forEach((prod) => products.push(prod));
          break;
        }
        case "manual": {
          fallback_products?.forEach((prod) => products.push(prod));
          break;
        }
      }

      switch (fallback_source) {
        case "complementary": {
          complementary_products?.forEach((prod) => products.push(prod));
          break;
        }
        case "related": {
          related_products?.forEach((prod) => products.push(prod));
          break;
        }
        case "recently_viewed": {
          const recent_products = await Promise.all(
            (window?._recent_products ?? []).map(([handle, product_id]) => _product.getHydratedProductData(handle, product_id))
          );

          recent_products?.forEach((prod) => products.push(prod));
          break;
        }
        case "manual": {
          fallback_products?.forEach((prod) => products.push(prod));
          break;
        }
      }

      const renderProducts =
        products
          ?.filter((prod) => {
            if (targeting_type === "product") {
              if (!prod || prod.id === state?.product?.id) return false;
            }
            if (/^cart_/gi.test(targeting_type)) {
              if (cart.state.items.some((item) => item.product_id === prod?.id)) return false;
            }
            if (/^recently_viewed_/gi.test(targeting_type)) {
              if (_recent_products.some(([handle, id]) => id === prod?.id)) return false;
            }
            if (filter_by_type) {
              if (prod.type.toLowerCase().trim() !== filter_by_type) return false;
            }
            if (hide_out_of_stock && !prod.available) {
              return false;
            }
            return true;
          })
          ?.filter((a, i, arr) => arr.findIndex((b) => b.id === a.id) === i) ?? [];

      const validFallbackProducts =
        fallback_products
          ?.filter((prod) => {
            if (targeting_type === "product") {
              if (!prod || prod.id === state?.product?.id) return false;
            }
            if (/^cart_/gi.test(targeting_type)) {
              if (cart.state.items.some((item) => item.product_id === prod?.id)) return false;
            }
            if (/^recently_viewed_/gi.test(targeting_type)) {
              if (_recent_products.some(([handle, id]) => id === prod?.id)) return false;
            }
            if (filter_by_type) {
              if (prod.type.toLowerCase().trim() !== filter_by_type) return false;
            }
            if (hide_out_of_stock && !prod.available) {
              return false;
            }
            return true;
          })
          ?.filter((a, i, arr) => arr.findIndex((b) => b.id === a.id) === i) ?? [];

      switch (hide_if_empty) {
        case "none": {
          if (renderProducts?.length === 0 && validFallbackProducts?.length) {
            validFallbackProducts.forEach((prod) => renderProducts.push(prod));
          }
          break;
        }
        case "section": {
          container.closest<HTMLElement>(`.shopify-section`)?.style?.setProperty?.("display", "block");
          container.closest(`.shopify-section`)?.classList.toggle("!hidden", renderProducts?.length === 0);
          break;
        }
        case "container": {
          container?.classList.toggle("!hidden", renderProducts?.length === 0);
          break;
        }
        case "block": {
          break;
        }
      }

      const existingItems = container.querySelectorAll(`[data-render-index*="${block_id}--"]`);
      const controlProducts = renderProducts?.filter(
        (prod, index) =>
          !container.querySelector(`[data-render-index="${block_id}--${index}"][data-product-handle="${prod.handle}"]`)
      );

      if (!controlProducts.length && existingItems.length === renderProducts?.length) {
        return;
      }

      existingItems.forEach((el) => el.remove());

      renderProducts.slice(0, limit).forEach((prod, i, arr) => {
        const node = document.querySelector(`[data-product-card='${product_class}']`)?.cloneNode(true) as HTMLElement;

        if (node) {
          const div = document.createElement("div");
          div.setAttribute("data-product-handle", prod.handle);
          div.setAttribute("data-style-id", `${section_id}--${block_id}`);
          div.setAttribute("data-render-index", `${block_id}--${i}`);
          div.setAttribute(
            "class",
            `shrink-0 max-w-full w-full order-[--order] ${
              i > desktop_display_limit ? "mobile-only" : i > mobile_display_limit ? "desktop-tablet-only" : ""
            }`
          );
          div.style.setProperty("--order", `${(i + order_offset) * 10}`);
          node.removeAttribute(`data-product-card`);
          node.setAttribute("data-product-handle", prod.handle);
          node.setAttribute("data-product-id", `${prod.id}`);
          node.classList.add("card-loading");
          if (addon_products) {
            node.setAttribute("data-addon-product-target", addon_target_product);
            if (addon_auto_add) {
              node.setAttribute("data-addon-auto-add", "");
            }
            if (addon_bundle_in_cart) {
              node.setAttribute("data-addon-cart-bundle", "");
            }
          }
          node.querySelectorAll("[data-loop-item], [data-x-if], style").forEach((el) => el.remove());
          div.appendChild(node);
          container.appendChild(div);
        }
      });
      handleResize();
    };

    renderDynamicProductCards();

    if (targeting_type === "product" && (primary_source === "related" || fallback_source === "related")) {
      Alpine.effect(() => {
        if (state.product?._recommendations_loaded_at || state.product?.complementary_products?.length) {
          renderDynamicProductCards();
        }
      });
    }

    if (/^cart_/gi.test(targeting_type)) {
      Alpine.effect(() => {
        const show =
          !container.closest("[data-dynamic-modals]") ||
          editor?.select_section_id === section_id ||
          modal.id === "modal--cart-drawer";

        if ((cart.state.total_price || cart.state.item_count || cart.state.item_count <= 0) && show) {
          renderDynamicProductCards();
        }
      });
    }

    _product.getHydratedProductData(product?.handle, product?.id).then((res) => {
      state.product = state.product?.handle === res?.handle ? res : state.product;
    });
  },
  getSelectedVariant: (product: _Product_hydrated, variantId?: number) => {
    const {
      variants = [],
      selected_variant_id,
      selected_or_first_available_variant_id,
      options_with_values = [],
    } = product ?? {};

    const matchDepth = (v: _Variant_hydrated, depth: number) =>
      options_with_values
        .slice(0, depth)
        .every((option, i) => _product.lastOptions[option?.name?.toLowerCase()] === v.options[i]);

    const matchSome = (v: _Variant_hydrated) =>
      options_with_values.some((option, i) => _product.lastOptions[option?.name?.toLowerCase()] === v.options[i]);

    const strategies: ((v: _Variant_hydrated) => boolean)[] = [
      (v) => v.id === variantId,
      (v) => v.id && v.available && !v.preorder && matchDepth(v, options_with_values.length),
      (v) => v.id && v.available && !v.preorder && matchDepth(v, options_with_values.length - 1),
      (v) => v.id && v.available && !v.preorder && matchDepth(v, options_with_values.length - 2),
      (v) => v.id && v.available && !v.preorder && matchSome(v),
      (v) => v.id === selected_variant_id && v.available && !v.preorder,
      (v) => v.id === selected_or_first_available_variant_id && v.available && !v.preorder,
      (v) => v.id && v.available && !v.preorder,
      (v) => v.id && v.available && matchDepth(v, options_with_values.length),
      (v) => v.id && v.available && matchDepth(v, options_with_values.length - 1),
      (v) => v.id && v.available && matchDepth(v, options_with_values.length - 2),
      (v) => v.id && v.available && matchSome(v),
      (v) => v.id === selected_variant_id && v.available,
      (v) => v.id === selected_or_first_available_variant_id && v.available,
      (v) => v.id && v.available,
    ];

    return strategies.map((fn) => variants.find(fn)).find(Boolean) || variants[0];
  },
  lastOptions: (JSONParse(sessionStorage.getItem("_p_last_options") || "{}") ?? {}) as Record<string, string>,
};

window._product = _product;

export const initProductData = () => {};
